import { useEffect, useRef, useState } from "react";
import { Check, Share, X } from "lucide-react";
import { useI18n } from "./i18n";

export type InstallResult = "installed" | "dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installPromptSubscribers = new Set<(event: BeforeInstallPromptEvent | null) => void>();

function publishInstallPrompt(event: BeforeInstallPromptEvent | null) {
  deferredInstallPrompt = event;
  installPromptSubscribers.forEach((subscriber) => subscriber(event));
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    publishInstallPrompt(event as BeforeInstallPromptEvent);
  });
}

export function isStandaloneApp() {
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone,
  );
}

export function isIosDevice() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

export function useInstallFlow() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(deferredInstallPrompt);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const pendingResolve = useRef<((result: InstallResult) => void) | null>(null);

  useEffect(() => {
    function markInstalled() {
      publishInstallPrompt(null);
      pendingResolve.current?.("installed");
      pendingResolve.current = null;
      setInstructionsOpen(false);
    }
    installPromptSubscribers.add(setPromptEvent);
    setPromptEvent(deferredInstallPrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      installPromptSubscribers.delete(setPromptEvent);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  async function requestInstall(): Promise<InstallResult> {
    if (isStandaloneApp()) return "installed";
    const availablePrompt = promptEvent ?? deferredInstallPrompt;
    if (availablePrompt) {
      publishInstallPrompt(null);
      await availablePrompt.prompt();
      const choice = await availablePrompt.userChoice;
      return choice.outcome === "accepted" ? "installed" : "dismissed";
    }
    if (!isIosDevice()) return "dismissed";
    setInstructionsOpen(true);
    return new Promise((resolve) => {
      pendingResolve.current = resolve;
    });
  }

  function closeInstructions() {
    setInstructionsOpen(false);
    pendingResolve.current?.("dismissed");
    pendingResolve.current = null;
  }

  return {
    canInstall: Boolean(promptEvent ?? deferredInstallPrompt) || isIosDevice() || isStandaloneApp(),
    requestInstall,
    instructionsOpen,
    closeInstructions,
  };
}

export function InstallDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  if (!open) return null;
  const ios = isIosDevice();
  const standalone = isStandaloneApp();
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="edit-dialog install-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("install.dialog.title")}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="icon-button dialog-close" type="button" aria-label={t("common.close")} onClick={onClose}>
          <X />
        </button>
        {standalone ? (
          <><Check className="install-dialog-icon" /><h2>{t("install.installed.title")}</h2><p>{t("install.installed.description")}</p></>
        ) : ios ? (
          <><Share className="install-dialog-icon" /><h2>{t("install.ios.title")}</h2><ol className="install-steps"><li><span>1</span><p>{t("install.ios.step1")}</p></li><li><span>2</span><p>{t("install.ios.step2")}</p></li></ol><p className="subtle-note">{t("install.ios.note")}</p></>
        ) : (
          <><h2>{t("install.browser.title")}</h2><p>{t("install.browser.description")}</p></>
        )}
        <button className="primary-button" type="button" onClick={onClose}>{t("common.confirm")}</button>
      </section>
    </div>
  );
}
